import {
  Alert,
  Button,
  Checkbox,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { getTeamSubmissionEntries, SubmissionEntry } from 'actions/challenge';
import {
  fetchTeam,
  generateInviteCode,
  joinTeam,
  leaveTeam,
  updateTeam,
} from 'actions/team';
import CreateTeamForm from 'components/CreateTeamForm';
import OpenSourceForm from 'components/CreateTeamForm/openSourceForm';
import Loading from 'components/Loading';
import SubmissionsList from 'components/SubmissionsList';
import LeaderboardContext from 'contexts/leaderboard';
import UserContext from 'contexts/user';
import DefaultLayout from 'layouts/default';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import RefreshIcon from '@mui/icons-material/Refresh';

import { Team, TeamAndUsers } from 'types/team';
import { Registration } from 'types/user';
import './index.css';
import { handleError } from 'actions/error';
import { MUIDataTableState, MUISortOptions } from 'mui-datatables';
const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 12,
  p: 4,
};
const userStyle = {
  bgcolor: 'background.paper',
  boxShadow: 1,
  borderRadius: 1,
  mr: 2,
  p: 1,
};
const ChallengeTeamPage = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const action = (snackbarId: string) => (
    <>
      <Button
        color="inherit"
        onClick={() => {
          closeSnackbar(snackbarId);
        }}
      >
        Dismiss
      </Button>
    </>
  );
  const history = useHistory();
  const { leaderboards, getChallenge } = useContext(LeaderboardContext);
  const { getRegistration } = useContext(UserContext);
  const params = useParams<{ id: string }>();
  const { search } = useLocation();
  const [registration, setRegistration] = useState<Registration>();
  const [team, setTeam] = useState<TeamAndUsers>();
  const [submissionData, setSubmissionData] = useState<{
    submissions: SubmissionEntry[];
    total: number;
  }>({ submissions: [], total: 0 });
  const [displayLeaveModal, setDisplayLeaveModal] = useState(false);
  const [leaveTeamDisabled, setLeaveTeamDisabled] = useState(true);
  const [displayEditTeamModal, setDisplayEditTeamModal] = useState(false);
  const [displayInviteModal, setDisplayInviteModal] = useState(false);
  const [fetchingSubmissions, setFetchingSubmissions] = useState(false);
  const [openSource, setOpenSource] = useState<string>(
    'Yes, training code, evaluation code, and trained models.'
  );
  const handleOpenSourceSelection = (e: any) => {
    setOpenSource(e.target.value);
  };
  useEffect(() => {
    getChallenge([params.id]);
    getRegistration(params.id)
      .then((registration) => {
        setRegistration(registration);
      })
      .catch((err) => handleError(enqueueSnackbar, err));
    fetchTeam(params.id)
      .then((team) => {
        setTeam(team);
      })
      .catch((err) => handleError(enqueueSnackbar, err));
  }, [enqueueSnackbar, params.id]);

  const [dataOptions, setDataOptions] = useState<{
    page: number;
    sortOrder: MUISortOptions;
    rowsPerPage: number;
    sortKey: string;
  }>({
    page: 0,
    sortOrder: { name: 'Submission Date', direction: 'desc' },
    rowsPerPage: 10,
    sortKey: 'submissionDate',
  });
  const dataHandler = (tableState: MUIDataTableState, sortKey: string) => {
    setDataOptions({
      page: tableState.page,
      sortOrder: tableState.sortOrder,
      rowsPerPage: tableState.rowsPerPage,
      sortKey,
    });
  };
  // useEffect(() => {
  //   getChallenge([params.id]);
  //   let sortOrder = -1;
  //   if (dataOptions.sortOrder.direction === "asc") sortOrder = 1;
  //   getAllSubmissionEntries(params.id, dataOptions.rowsPerPage, dataOptions.page, dataOptions.sortKey, sortOrder).then((data) => {
  //     setSubmissionData(data);
  //   });
  // }, [dataOptions]);
  useEffect(() => {
    if (team) {
      getTeamSubmissionEntries(
        params.id,
        team.id,
        dataOptions.rowsPerPage,
        dataOptions.page,
        dataOptions.sortKey,
        dataOptions.sortOrder.direction
      )
        .then((data) => {
          setSubmissionData(data);
        })
        .catch((err) => handleError(enqueueSnackbar, err));
    }
  }, [team, params, enqueueSnackbar, dataOptions]);
  useEffect(() => {
    const values = queryString.parse(search);
    if (values.team_id && values.invite_code) {
      //@ts-ignore
      joinTeam(params.id, values.team_id, values.invite_code)
        .then((data) => {
          //reload page. TODO - should update state instead if possible.
          history.push(`/challenges/${params.id}/team`);
          // eslint-disable-next-line no-restricted-globals
          location.reload();
        })
        .catch((err) => handleError(enqueueSnackbar, err));
    }
  }, [enqueueSnackbar, history, params.id, search]);
  const leaderboard = leaderboards[params.id];
  if (!leaderboard || !registration || (registration.teamId && !team)) {
    return (
      <DefaultLayout>
        <Loading text="Loading Information" />
      </DefaultLayout>
    );
  }
  const noTeam = !registration.teamId;
  const handleLeaveTeam = () => {
    if (team) {
      leaveTeam(params.id, team.id)
        .then(() => {
          enqueueSnackbar('Left Team', { variant: 'info' });
          history.push(`/challenges/${params.id}`);
        })
        .catch((err) => handleError(enqueueSnackbar, err));
    }
  };
  const handleEditTeam = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let data = new FormData(e.target as HTMLFormElement);
    let teamName = data.get('teamName')!.toString();
    updateTeam(params.id, team!.id, teamName, { openSource })
      .then((data) => {
        enqueueSnackbar('Updated team successfully', {
          variant: 'success',
        });
        setTeam(data);
      })
      .catch((err) => handleError(enqueueSnackbar, err))
      .finally(() => {
        setDisplayEditTeamModal(false);
      });
  };
  const handleClearFinalSubmissionSelection = () => {
    if (team) {
      const newTeam: TeamAndUsers = { ...team };
      newTeam.meta['finalSubmission'] = {};
      updateTeam(params.id, team.id, newTeam.teamName, { ...newTeam.meta })
        .then((data) => {
          // TODO MOVE THIS OUT
          enqueueSnackbar('Cleared final submission selection', {
            variant: 'success',
            action,
          });
          setTeam(data);
        })
        .catch((err) => handleError(enqueueSnackbar, err))
        .finally(() => {
          setDisplayEditTeamModal(false);
        });
    }
  }
  const generateNewInviteCode = () => {
    if (team) {
      generateInviteCode(params.id, team.id).then(setTeam);
    }
  };
  const refreshSubmissions = () => {
    if (team) {
      setFetchingSubmissions(true);
      getTeamSubmissionEntries(params.id, team.id)
        .then((data) => {
          setSubmissionData(data);
        })
        .catch((err) => handleError(enqueueSnackbar, err))
        .finally(() => {
          setFetchingSubmissions(false);
        });
    }
  };
  const handleFinalSubmissionTick = (
    checked: boolean,
    track: string,
    submission: SubmissionEntry
  ) => {
    const submissionId = submission.id;
    if (team) {
      const newTeam: TeamAndUsers = { ...team };
      if (!newTeam.meta['finalSubmission']) {
        newTeam.meta['finalSubmission'] = {};
      }
      if (!newTeam.meta['finalSubmission'][track]) {
        newTeam.meta['finalSubmission'][track] = [];
      }
      // if (submission.entry.tasks.length)
      // confirm()

      if (checked) {
        if (
          newTeam.meta['finalSubmission'][track].length >=
          leaderboard.settings.maxfinalsubmissions
        ) {
          enqueueSnackbar(
            `You may only select a maximum of ${leaderboard.settings.maxfinalsubmissions} submissions for final evaluation for track ${track}`,
            {
              variant: 'error',
              action,
            }
          );
          return;
        }
        newTeam.meta['finalSubmission'][track].push(submissionId);
      } else {
        newTeam.meta['finalSubmission'][track].splice(
          newTeam.meta['finalSubmission'][track].indexOf(submissionId),
          1
        );
      }
      updateTeam(params.id, team!.id, newTeam.teamName, { ...newTeam.meta })
        .then((data) => {
          // TODO MOVE THIS OUT
          enqueueSnackbar('Saved selection', {
            variant: 'success',
            action,
          });
          setTeam(data);
        })
        .catch((err) => handleError(enqueueSnackbar, err))
        .finally(() => {
          setDisplayEditTeamModal(false);
        });
    }
  };
  const isSubmissionEntryCheckedForFinalSubmission = (
    value: SubmissionEntry
  ) => {
    const tags = value.tags ?? [];
    let checked = false;
    if (team && tags.length > 0) {
      const track = tags[0];
      if (team.meta['finalSubmission'] && team.meta['finalSubmission'][track]) {
        const trackData = team.meta['finalSubmission'][track];
        if (trackData.indexOf(value.id) !== -1) {
          checked = true;
        }
      }
    }
    return checked;
  };
  const prependColumns = [
    {
      name: 'Final Submission',
      options: {
        filter: false,
        sortCompare: (order: string) => {
          return (
            obj1: { data: SubmissionEntry },
            obj2: { data: SubmissionEntry }
          ) => {
            let val1 = isSubmissionEntryCheckedForFinalSubmission(obj1.data)
              ? 1
              : 0;
            let val2 = isSubmissionEntryCheckedForFinalSubmission(obj2.data)
              ? 1
              : 0;
            console.log(order, { val1, obj1 }, { obj2, val2 });
            return (val1 - val2) * (order === 'asc' ? 1 : -1);
          };
        },
        sort: false,
        customBodyRender: (
          value: SubmissionEntry,
          tableMeta: any,
          updateValue: any
        ) => {
          const tags = value.tags ?? [];
          if (team && tags.length > 0) {
            const track = tags[0];
            const checked = isSubmissionEntryCheckedForFinalSubmission(value);
            return (
              <div className="final-check">
                <Checkbox
                  checked={checked}
                  onClick={(e: any) => {
                    handleFinalSubmissionTick(e.target.checked, tags[0], value);
                  }}
                ></Checkbox>
              </div>
            );
          }
          return <></>;
        },
      },
    },
  ];
  const prependData = (d: SubmissionEntry) => {
    return [d];
  };

  return (
    <DefaultLayout>
      <div className="ChallengeTeamPage">
        <Typography variant="h3">Team</Typography>
        {noTeam && (
          <>
            <Typography variant="body1">
              <strong>Challenge:</strong> {leaderboard.name}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              You are not part of a team yet. Join a team by clicking a team
              invite link or create your own team
            </Typography>
            <CreateTeamForm challengeId={params.id} />
          </>
        )}
        {!noTeam && team && (
          <>
            <br />
            <Typography variant="body1">
              <strong>Challenge:</strong> {leaderboard.name}
            </Typography>
            <Typography variant="body1">
              <strong>Team Name:</strong> {team.teamName}
            </Typography>
            <div>
              <Typography variant="body1">
                <strong>Open Source:</strong> {team.meta.openSource}
              </Typography>
            </div>
            <div>
              <Typography variant="body1">
                <strong>Team Members</strong>
              </Typography>
              {[...team.users].map((user) => {
                return (
                  <Box sx={userStyle} key={user.name} className="user-box">
                    <Typography variant="body1">{user.name}</Typography>
                  </Box>
                );
              })}
            </div>
            <br />
            <>
              {registration?.teamId && !leaderboard.settings.submissions && (
                <Button
                  sx={{ mr: 1 }}
                  className="challenge-action-btn"
                  variant="contained"
                  color="primary"
                  disabled={
                    !registration?.teamId || !leaderboard.settings.submissions
                  }
                >
                  Submissions are disabled
                </Button>
              )}
              {registration?.teamId && leaderboard.settings.submissions && (
                <Link to={`/challenges/${params.id}/submit`}>
                  <Button
                    sx={{ mr: 1 }}
                    className="challenge-action-btn"
                    variant="contained"
                    color="primary"
                    disabled={
                      !registration?.teamId || !leaderboard.settings.submissions
                    }
                  >
                    Create Submission {registration?.teamId && `For Your Team`}
                  </Button>
                </Link>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  setDisplayEditTeamModal(true);
                }}
                sx={{ mr: 1 }}
              >
                Edit Team Information
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setDisplayInviteModal(true);
                }}
              >
                Team Invite Code
              </Button>
            </>
            <br />
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setDisplayLeaveModal(true);
              }}
              sx={{ mt: 1 }}
            >
              Leave Team
            </Button>
            <Modal
              open={displayInviteModal}
              onClose={() => {
                setDisplayInviteModal(false);
              }}
              aria-labelledby="modal-modal-title"
            >
              <Box sx={style}>
                <Typography id="modal-modal-title" variant="h4" component="h4">
                  Team Invite Code
                </Typography>
                <Typography variant="body2">
                  Have team members login to the website and open this link to
                  join your team. Do not share this with people outside of your
                  team.
                </Typography>
                <TextField
                  className="modal-invite-code"
                  id="modal-invite-code"
                  sx={{ mt: 1, mb: 1 }}
                  multiline
                  value={`${window.location.href}?team_id=${team.id}&invite_code=${team.inviteId}`}
                ></TextField>

                <Button
                  sx={{ width: '100%' }}
                  variant="contained"
                  onClick={() => {
                    generateNewInviteCode();
                  }}
                >
                  Generate new Team Invite Code
                </Button>
              </Box>
            </Modal>
            <Modal
              open={displayEditTeamModal}
              onClose={() => {
                setDisplayEditTeamModal(false);
              }}
              aria-labelledby="modal-modal-title"
            >
              <Box sx={{ ...style, width: 600, maxWidth: 'calc(100% - 4rem)' }}>
                <Typography id="modal-modal-title" variant="h4" component="h4">
                  Edit Team
                </Typography>
                <form onSubmit={handleEditTeam}>
                  <TextField
                    className="name-input"
                    color="primary"
                    label="Team name (visible to everyone)"
                    name="teamName"
                    type="text"
                    required
                    defaultValue={team.teamName}
                    sx={{ mt: 2, mb: 2, width: '100%' }}
                  />
                  <OpenSourceForm
                    handleOpenSourceSelection={handleOpenSourceSelection}
                    openSource={openSource}
                  />
                  <Button
                    sx={{ width: '100%' }}
                    variant="contained"
                    type="submit"
                  >
                    Update Team Information
                  </Button>
                </form>
              </Box>
            </Modal>
            <Modal
              open={displayLeaveModal}
              onClose={() => {
                setDisplayLeaveModal(false);
              }}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box sx={style}>
                <Typography id="modal-modal-title" variant="h4" component="h4">
                  Leave Team
                </Typography>
                <Typography
                  id="modal-confirmation-warning"
                  sx={{ mt: 1, mb: 1 }}
                  variant="body2"
                >
                  Any submissions and results submitted to this team will not
                  transfer over to any new teams you join or yourself.
                </Typography>
                {team.userIds.length === 1 && (
                  <Alert severity="warning">
                    You are the last member of this team. Leaving this team will
                    remove it entirely from the leaderboard and void all
                    submissions made for the team.
                  </Alert>
                )}
                <Typography
                  id="modal-confirmation-reminder"
                  sx={{ mt: 1, mb: 1 }}
                  variant="body2"
                >
                  Please type your team name <strong>{team.teamName}</strong> to
                  confirm
                </Typography>
                <TextField
                  className="name-input"
                  color="primary"
                  name="teamName"
                  type="text"
                  required
                  size="small"
                  onChange={(e) => {
                    setLeaveTeamDisabled(e.target.value !== team.teamName);
                  }}
                  sx={{ mb: 1, width: '100%' }}
                />
                <br />
                <Button
                  sx={{ width: '100%' }}
                  variant="contained"
                  color="error"
                  onClick={handleLeaveTeam}
                  disabled={leaveTeamDisabled}
                >
                  I understand, I wish to leave this team.
                </Button>
              </Box>
            </Modal>
            <br />
            <br />
            <Typography variant="h4" sx={{ mb: 1 }}>
              Team Submissions{' '}
              <>
                <IconButton
                  disabled={fetchingSubmissions}
                  className={fetchingSubmissions ? 'rotating' : ''}
                  onClick={() => {
                    refreshSubmissions();
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </>
            </Typography>
            <Typography variant="body2">
              Below shows all of your team's past submissions. Results shown are
              for stage 1 of evaluation. You can select up to two submissions
              per track for final evaluation here. Note that for final
              evaluation in stage 2,{' '}
              <strong>
                you must ensure that your final submissions cover all tasks in
                the track
              </strong>{' '}
              (you can test it by ticking all task boxes on the submission page)
              or else you may not get a score for certain tasks.
              
            </Typography>
            <Typography variant="body2">
            To unselect out all final submission selections for all tracks click below:
            </Typography>
            <Typography variant="body2">
            <Button
              // sx={{ width: '100%' }}
              size='small'
              variant="contained"
              color="error"
              onClick={handleClearFinalSubmissionSelection}
            >
              Clear Final Submission Selection
            </Button>
            </Typography>
            <br/>
            {leaderboard && (
              <SubmissionsList
                data={submissionData.submissions}
                total={submissionData.total}
                dataHandler={dataHandler}
                rowsPerPage={dataOptions.rowsPerPage}
                showProgress
                metrics={leaderboard.metrics}
                prependColumns={prependColumns}
                prependData={prependData}
              />
            )}
          </>
        )}
      </div>
    </DefaultLayout>
  );
};

export default ChallengeTeamPage;
